/**
 * `POST /api/multi/v1/captures/*` — Multi's capture wire contract (APP-8, ADR 0117).
 *
 * Shared because BOTH sides parse it: the browser parses what the gateway returns, and the
 * gateway parses what the browser pushes. Services never import each other (ADR 0001), so a
 * schema two of them must agree on lives here or it gets written twice and drifts.
 *
 * The one field deliberately NOT on the wire is `authorId`. The browser is untrusted (ADR 0117
 * §4): the gateway derives the owner from the verified Access identity and stamps it, so a
 * client that sent one would be sending a value the server must ignore — and a field the server
 * ignores is a field that eventually gets trusted by accident.
 */
import { z } from 'zod';

import { MULTI_DISPATCH_EVENTS, type MultiDispatchIntent } from '../integration/registry.js';
import { NoteStructureSchema } from './multi-note-structure-v1.js';

/**
 * The intents, DERIVED from the intent → event map rather than spelled again.
 *
 * This union used to be a literal `z.enum(['links', 'idea'])`, one of seven places the same pair
 * was written out. Adding a third intent meant editing all seven and hoping; a wire schema that
 * disagreed with the map would reject a dispatch the device had already recorded, and the operator
 * would see a capture that stuck at "queued to send" for ever. Derived, that disagreement cannot
 * be expressed.
 */
const DISPATCH_INTENTS = Object.keys(MULTI_DISPATCH_EVENTS) as [MultiDispatchIntent, ...MultiDispatchIntent[]];

/**
 * One capture as it crosses the wire.
 *
 * `updatedAt` is load-bearing rather than informational: it is the value last-writer-wins
 * compares on (ADR 0117 §3). `createdAt` is not — a device's clock decides when its own capture
 * happened, and that never conflicts because ids are minted per-device.
 */
export const MultiCaptureWireSchema = z.object({
  /** Device-minted ULID. The idempotency key for the whole sync path. */
  id: z.string().min(1).max(64),
  audience: z.literal('private'),
  type: z.string().min(1).max(32),
  title: z.string().max(512),
  body: z.string().max(20_000),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  sorted: z.boolean(),
  binned: z.boolean(),
  /**
   * What the operator asked be done with it. Absent — or empty — means "kept, not sent".
   *
   * A SET, because the routing map lets one capture be armed down several branches at once: a
   * thought that cites a competitor's pricing page is genuinely both an idea to test and a link to
   * go and look at, and the operator should not have to pick. The gateway derives one event per
   * entry, each with its own event id, in the capture's own transaction.
   *
   * **A bare string is still accepted, and normalised.** That is not politeness — it is every
   * capture already in Postgres and on every device that synced before this shipped, and a client
   * rolled back to the previous bundle keeps sending them.
   *
   * OPTIONAL and additive: every capture written before this field existed parses unchanged, and
   * an older device that has never heard of it keeps syncing.
   */
  dispatch: z
    .preprocess((value) => (typeof value === 'string' ? [value] : value), z.array(z.enum(DISPATCH_INTENTS)).max(8))
    .optional(),
  /**
   * When this capture reached a terminal state. Absent means it is still live.
   *
   * Optional and additive for the same reason as `dispatch`: rows written before it existed parse
   * unchanged, and a device that has never heard of it keeps syncing. It needs no migration —
   * `multi.captures` stores the record as a `jsonb` blob, so there is no column to add.
   *
   * Device-owned, like `sorted` and `binned`: retiring is a decision the operator makes on a
   * device, and the server only carries it between them.
   */
  retiredAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * When this device last looked for notes related to this one (APP-8 §3.4). Absent means nothing
   * has.
   *
   * Optional and additive for the same reason as `retiredAt`, and it needs no migration for the
   * same reason: the record is a `jsonb` blob.
   *
   * Carried between devices deliberately, even though the suggestions themselves arrive as links.
   * Without it every device re-asks a model about every note the others have already covered — the
   * same work, at N times the cost, producing connections that already exist.
   */
  suggestedAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * What something MADE of this note — derived, never a replacement for it (ADR 0127).
   *
   * `title` and `body` above are untouched and still required: this rides beside them, and the
   * card resolves `structure?.title ?? title`. So a capture that was never structured — which is
   * every row written before this shipped, and every row from a device on an older bundle —
   * parses unchanged and renders exactly as it does today.
   *
   * Device-owned on the wire like `sorted` and `retiredAt`, even though the gateway is what
   * produces it: the device asks for a structure, stores what came back, and syncs it as part of
   * its own row under the same last-writer-wins rule. Making it server-owned would need a second
   * channel and a second conflict story for a field that changes once in a capture's life.
   *
   * `null` and absent both mean "not structured" and both are correct — absent is a row that
   * predates the field, `null` is a device saying so explicitly.
   */
  structure: NoteStructureSchema.nullable().optional(),
  /**
   * Which of this note's actions have been ticked off (`<captureId>#<index>`).
   *
   * Device-owned filing, like `sorted` and `retiredAt`: a decision the operator makes on a device,
   * which the server only carries between them. Optional and additive, so every row already in
   * Postgres parses unchanged and a device on an older bundle keeps syncing.
   *
   * Bounded at 12 to match `NoteStructure.actions` — a note cannot have more ticked actions than
   * it has actions, and an unbounded array on a device-supplied field is an unbounded row.
   */
  actionsDone: z.array(z.string().min(1).max(80)).max(12).optional(),
});
export type MultiCaptureWire = z.infer<typeof MultiCaptureWireSchema>;

/**
 * How far a capture's DISPATCH has actually got — server-owned, and never sent by the device.
 *
 * The card used to have one word for two different facts. Its sync state proves the capture reached
 * the SERVER; it says nothing about whether the event was published, which is one hop further on.
 * "Sent" that means "saved" is exactly the overstatement APP-8 exists to remove, so the two are
 * separated here and the copy is derived from this rather than from `sorted`.
 *
 * `null` (the field absent) is the default and means no intent was ever recorded — writing
 * something down is not sending it.
 */
export const MultiDispatchStateSchema = z.enum(['queued', 'sent', 'failed']);
export type MultiDispatchState = z.infer<typeof MultiDispatchStateSchema>;

/**
 * What the server knows about one capture's dispatch, returned on both the push and the pull.
 *
 * Carried BESIDE the capture rather than on it: the capture is the device's row and travels
 * last-writer-wins, while this is the gateway's own account of an outbox row. Folding it into
 * `MultiCaptureWire` would let a device push a value the server must then ignore — and a field the
 * server ignores is a field that eventually gets trusted by accident.
 */
export const MultiDispatchStatusSchema = z.object({
  captureId: z.string().min(1).max(64),
  state: MultiDispatchStateSchema,
  /** Which event was queued. Present so a client can say WHAT was sent without re-deriving it. */
  event: z.string().min(1).max(120),
  /** Why the last publish attempt failed. Present only with `failed`, and shown to the operator. */
  error: z.string().max(500).nullable(),
});
export type MultiDispatchStatus = z.infer<typeof MultiDispatchStatusSchema>;

/**
 * A sync push: the local outbox's pending rows, drained in one call.
 *
 * Bounded at 200 deliberately — an unbounded batch is a request whose size an offline device
 * decides, and a phone that has been in a tunnel for a week must drain in slices rather than in
 * one request that times out and retries forever.
 */
/**
 * A connection between two captures, on the wire (APP-8 §3.2).
 *
 * Device-owned in full: the operator draws these on a phone and the server only carries them
 * between devices, exactly as it does a capture's `sorted`/`binned`.
 *
 * `deletedAt` is a TOMBSTONE and is the reason this row is ever pushed after it is undone: a
 * disconnection that arrived as an absent row would be indistinguishable from a device that had
 * never made the link, and the next push from the other phone would restore it.
 */
export const MultiLinkWireSchema = z.object({
  id: z.string().min(1).max(64),
  fromId: z.string().min(1).max(64),
  /** `null` while the link dangles — it names something that does not exist yet. */
  toId: z.string().min(1).max(64).nullable(),
  toText: z.string().max(512),
  origin: z.enum(['text', 'picker', 'voice', 'suggested']),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  deletedAt: z.number().int().nonnegative().nullable().optional(),
});
export type MultiLinkWire = z.infer<typeof MultiLinkWireSchema>;

export const MultiSyncRequestSchema = z.object({
  captures: z.array(MultiCaptureWireSchema).max(200),
  /**
   * Optional and additive: a device that has never heard of links keeps syncing, and its absence
   * means "this push says nothing about connections" rather than "there are none".
   *
   * Bounded like captures, and for the same reason — one request that times out and retries
   * forever is worse than two that land.
   */
  links: z.array(MultiLinkWireSchema).max(200).optional(),
});
export type MultiSyncRequest = z.infer<typeof MultiSyncRequestSchema>;

/**
 * What the server made of each pushed row.
 *
 * `applied` — the push won (it was new, or newer than the stored row).
 * `superseded` — the STORED row won on last-writer-wins, and `capture` is what the client must
 *   adopt. Reported rather than silently dropped: the client just showed the operator a change
 *   that is not going to survive, and it has to be able to put the real one back on screen.
 *
 * ONE OUTCOME PER DISTINCT ID, not one per array entry. A queue that records one row per CHANGE
 * (create, then file) legitimately drains two entries for one capture in a single batch; only the
 * latest can win, so reporting both would report a decision that was never made. Clients key
 * outcomes by id.
 */
export const MultiSyncOutcomeSchema = z.object({
  id: z.string().min(1),
  status: z.enum(['applied', 'superseded']),
  capture: MultiCaptureWireSchema,
});
export type MultiSyncOutcome = z.infer<typeof MultiSyncOutcomeSchema>;

export const MultiSyncResponseSchema = z.object({
  outcomes: z.array(MultiSyncOutcomeSchema),
  /**
   * The dispatch state of every capture in this push that has one. Absent from the array means no
   * intent — which is the default and is not an error.
   */
  dispatches: z.array(MultiDispatchStatusSchema),
});
export type MultiSyncResponse = z.infer<typeof MultiSyncResponseSchema>;

/** Ask for this owner's captures. The owner is never a parameter — it comes from the token. */
export const MultiListRequestSchema = z.object({
  /**
   * Deliver everything after this cursor. Omit to start from the beginning.
   *
   * SERVER-ASSIGNED, and that is the whole design. The obvious alternative — "everything changed
   * after this timestamp" — uses `updatedAt`, which comes from the DEVICE. A phone whose clock is
   * an hour fast writes a capture stamped `now + 1h`; any device that pulls it advances its cursor
   * past every capture the next hour will produce, and they are never delivered. No error, no
   * failed request, and a manually-set clock makes it permanent.
   *
   * `updatedAt` remains the CONFLICT rule (ADR 0117 §3) — last-writer-wins on a client clock is a
   * recorded choice whose failure is bounded and visible. Using the same untrusted number for
   * DELIVERY is a different failure: it silently stops delivery. Two concerns, one column, now
   * separated. Replaced `since`, which no client ever used.
   */
  cursor: z.number().int().nonnegative().optional(),
});
export type MultiListRequest = z.infer<typeof MultiListRequestSchema>;

export const MultiListResponseSchema = z.object({
  captures: z.array(MultiCaptureWireSchema),
  /**
   * This owner's connections, TOMBSTONES INCLUDED.
   *
   * Undone links are the reason a pull carries them at all: a connection that simply stopped
   * appearing would be indistinguishable from one this device had never heard of, and the device
   * would keep drawing a line the operator has already removed elsewhere.
   *
   * Optional so an older gateway's response still parses — absent means "this server says nothing
   * about connections", which is not the same as "there are none" and must not clear anything.
   */
  links: z.array(MultiLinkWireSchema).optional(),
  /**
   * True when the server held MORE rows than it returned.
   *
   * On the wire rather than implied, because the alternative is the worst kind of quiet failure:
   * a bounded query returns a short list, `error` is null, and a truncated answer is
   * indistinguishable from a complete one — so a device would render "these are all your
   * captures" while holding a prefix. The client must say so visibly and pull the rest.
   */
  truncated: z.boolean(),
  /**
   * Where to continue from. Pass it back as `cursor` on the next call.
   *
   * Returned even when `captures` is empty, so a caller that has caught up keeps a valid place to
   * resume from rather than falling back to the beginning and re-fetching an entire history.
   */
  nextCursor: z.number().int().nonnegative(),
  /**
   * Dispatch state for the returned captures that have one — the half of "sent" the device cannot
   * know on its own. A capture whose event has published comes back `sent`; one still in the
   * outbox comes back `queued`; one whose publish failed comes back `failed` WITH the reason, so
   * the card can say what went wrong instead of showing a tick that is not true.
   */
  dispatches: z.array(MultiDispatchStatusSchema),
});
export type MultiListResponse = z.infer<typeof MultiListResponseSchema>;
